import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Title from '../../components/common/Title';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';
import Icon from '../../components/common/Icon';
import Loading from '../../components/common/Loading';
import Toast from '../../components/forms/Toast';
import InputField from '../../components/forms/InputField';
import { apiRequest, formatDate } from '../../utils';
import { readCart, clearCart, computeTotals } from './cart';
import { EVENT_KEY } from './Shop';

const money = (v, currency) => `${(Number(v) || 0).toFixed(2)} ${currency || ''}`.trim();
const cap = { textTransform: 'capitalize' };
const EMPTY_ADDR = { name: '', street: '', number: '', box: '', zip: '', city: '', country: '', vat: '' };

const Checkout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  // The edition the shop was showing when this basket was filled. Checkout must order
  // for THAT one — the shop sells several editions, and the basket is stored per edition.
  const [eventId] = useState(() => sessionStorage.getItem(EVENT_KEY) || null);
  const [cart] = useState(() => readCart(sessionStorage.getItem(EVENT_KEY) || null));
  const [addressId, setAddressId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [voucherLimit, setVoucherLimit] = useState(undefined); // fills "already used N times"
  const [showToast, setShowToast] = useState(false);

  // Only relevant when the shop has conditions; without a terms_url the box is
  // never rendered and this stays false without ever blocking the order.
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Optional promo code — sent at checkout; the server validates it and, if valid,
  // discounts one unit of the first qualifying line. An invalid code is rejected there.
  const [voucherCode, setVoucherCode] = useState('');

  // Inline "add address" form state.
  const [showAddr, setShowAddr] = useState(false);
  const [addr, setAddr] = useState(EMPTY_ADDR);
  const [savingAddr, setSavingAddr] = useState(false);

  // One idempotency key per checkout session — reused across retries so a
  // double-submit (or a retry after a flaky network) can never mint a 2nd order.
  const idemKey = useRef(
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  );

  useEffect(() => {
    (async () => {
      try {
        const res = await apiRequest('GET', `/shop${eventId ? `?event_id=${encodeURIComponent(eventId)}` : ''}`);
        setData(res);
        if (res.addresses?.length) setAddressId(res.addresses[0].id);
      } catch (err) {
        if ((err.message || '') === 'notAuthorized') { navigate('/home'); return; }
        setError(err.message || 'somethingWentWrong');
        setShowToast(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate, eventId]);

  const products = (data?.products || []).filter((p) => Number(cart[p.rate_id]) > 0);
  const currency = data?.currency;
  const termsUrl = data?.terms_url || null;
  const termsBlocked = Boolean(termsUrl) && !termsAccepted;
  const { subtotal, vat, total } = computeTotals(products, cart, data?.with_vat);

  const updateAddr = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));

  const saveAddress = async () => {
    if (savingAddr) return;
    if (!addr.street.trim() || !addr.zip.trim() || !addr.city.trim() || !addr.country.trim()) {
      setError('missingAddressFields'); setShowToast(true); return;
    }
    setSavingAddr(true);
    try {
      const created = await apiRequest('POST', '/shop/address', addr);
      setData((prev) => ({ ...prev, addresses: [...(prev.addresses || []), created], has_address: true }));
      setAddressId(created.id);
      setAddr(EMPTY_ADDR);
      setShowAddr(false);
    } catch (e) {
      setError(e.message || 'somethingWentWrong'); setShowToast(true);
    } finally {
      setSavingAddr(false);
    }
  };

  const placeOrder = async () => {
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const basket = products.map((p) => ({ rate_id: p.rate_id, qty: Number(cart[p.rate_id]) }));
      const res = await apiRequest('POST', '/shop/checkout', {
        basket,
        // Ordered against the edition the catalog came back for, not whatever the
        // session happens to point at. The server re-checks it against its own list.
        event_id: data?.event_id || eventId || undefined,
        address_id: addressId,
        idempotency_key: idemKey.current,
        terms_accepted: termsAccepted,
        voucher_code: voucherCode.trim() || undefined,
      });
      if (res.checkout_url) {
        // Online payment: pay on Stripe first. Keep the cart until we return
        // confirmed-paid (an abandoned payment must not empty it).
        window.location.href = res.checkout_url;
        return;
      }
      // Hand the result to the confirmation screen and clear the cart. Using
      // sessionStorage (not router state) so a refresh on the confirmation
      // page still shows the result and never re-POSTs.
      sessionStorage.setItem('shopLastOrder', JSON.stringify(res));
      clearCart(data?.event_id || eventId || null);
      navigate('/shop/confirmation');
    } catch (err) {
      setError(err.message || 'somethingWentWrong');
      setVoucherLimit(err.data?.voucher_limit);
      setShowToast(true);
      setSubmitting(false); // allow a retry (same idempotency key)
    }
  };

  return (
    <Base activeNav="shop">
      <Title>
        <Icon name="shopping-cart" theme="primary" className="me-2" style={{ fontSize: '1.4rem' }} />
        {t('checkout')}
      </Title>

      {loading && <Loading size="lg" />}

      {!loading && products.length === 0 && (
        <Card className="text-center">
          <Icon name="shopping-cart" theme="muted" style={{ fontSize: '1.6rem' }} />
          <p className="mt-2 mb-2">{t('emptyCart')}</p>
          <Button to="/shop" theme="primary">{t('shop')}</Button>
        </Card>
      )}

      {!loading && products.length > 0 && (
        <>
          <Card>
            {/* Name the edition being ordered for: the shop sells more than one, and
                two editions of the same event differ only by their date. */}
            {data?.event_name && (
              <div className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
                {t('orderingFor')}: <span style={{ fontWeight: 600 }}>{data.event_name}</span>
                {data.event_date ? ` · ${formatDate(data.event_date)}` : ''}
              </div>
            )}
            {products.map((p) => {
              const qty = Number(cart[p.rate_id]);
              return (
                <div key={p.rate_id} className="d-flex justify-content-between mb-2">
                  <span style={{ minWidth: 0 }}>
                    {p.name} <span className="text-muted">×{qty}</span>
                    {p.discount_pct ? (
                      <span className="badge bg-danger-subtle text-danger-emphasis ms-1" style={{ fontSize: '0.72rem' }}>
                        −{(Number(p.discount_pct) || 0).toFixed(0)}%
                      </span>
                    ) : null}
                  </span>
                  <strong className="ms-2 flex-shrink-0">{money((p.effective_price ?? p.unit_price) * qty, currency)}</strong>
                </div>
              );
            })}
            <hr className="my-2" />
            <div className="d-flex justify-content-between"><span>{t('subtotal')}</span><span>{money(subtotal, currency)}</span></div>
            {data?.with_vat && (
              <div className="d-flex justify-content-between text-muted"><span>{t('vat')}</span><span>{money(vat, currency)}</span></div>
            )}
            <div className="d-flex justify-content-between mt-1" style={{ fontSize: '1.1rem' }}>
              <span style={cap}>{t('total')}</span><strong>{money(total, currency)}</strong>
            </div>
          </Card>

          <Card>
            <div style={{ fontWeight: 600 }} className="mb-2">{t('billingAddress')}</div>
            {!data?.has_address && !showAddr && (
              <p className="text-muted mb-2">{t('noBillingAddress')}</p>
            )}
            {data?.addresses?.map((a) => (
              <label key={a.id} className="d-flex align-items-start mb-2" style={{ gap: '0.5rem', cursor: 'pointer' }}>
                <input type="radio" name="billing-address" className="mt-1"
                       checked={addressId === a.id} onChange={() => setAddressId(a.id)} />
                <span>
                  {a.name && <div style={{ fontWeight: 500 }}>{a.name}</div>}
                  <div className="text-muted" style={{ fontSize: '0.9rem' }}>
                    {[a.street, a.number].filter(Boolean).join(' ')}{(a.street || a.number) ? ', ' : ''}
                    {[a.zip, a.city].filter(Boolean).join(' ')}
                    {a.country ? `, ${a.country}` : ''}
                    {a.vat ? ` — ${a.vat}` : ''}
                  </div>
                </span>
              </label>
            ))}

            {!showAddr && (
              <Button theme="light" mb="0" silent styles={{ width: 'auto' }} onClick={() => setShowAddr(true)}>
                <Icon name="plus-small" className="me-2" />{t('addAddress')}
              </Button>
            )}

            {showAddr && (
              <div className="mt-2">
                <InputField label={t('addressName')} value={addr.name} onChange={updateAddr('name')} />
                <InputField label={t('street')} value={addr.street} onChange={updateAddr('street')} required />
                <InputField label={t('number')} value={addr.number} onChange={updateAddr('number')} />
                <InputField label={t('box')} value={addr.box} onChange={updateAddr('box')} />
                <InputField label={t('zip')} value={addr.zip} onChange={updateAddr('zip')} required />
                <InputField label={t('city')} value={addr.city} onChange={updateAddr('city')} required />
                <InputField label={t('country')} value={addr.country} onChange={updateAddr('country')} required />
                <InputField label={t('vatNumber')} value={addr.vat} onChange={updateAddr('vat')} />
                <div className="d-flex" style={{ gap: '0.5rem' }}>
                  <Button mb="0" disabled={savingAddr} styles={{ width: 'auto' }} onClick={saveAddress}>
                    {savingAddr ? t('loading') : t('save')}
                  </Button>
                  <Button mb="0" theme="light" styles={{ width: 'auto' }}
                          onClick={() => { setShowAddr(false); setAddr(EMPTY_ADDR); }}>
                    {t('cancel')}
                  </Button>
                </div>
              </div>
            )}
          </Card>

          <Card>
            <div style={{ fontWeight: 600 }} className="mb-2">{t('voucherCode')}</div>
            <InputField value={voucherCode} placeholder={t('voucherCodePlaceholder')}
                        onChange={(e) => setVoucherCode(e.target.value)} mb="0" />
          </Card>

          {termsUrl && (
            <Card>
              <label className="d-flex align-items-start" style={{ gap: '0.5rem', cursor: 'pointer' }}>
                <input type="checkbox" className="mt-1" checked={termsAccepted}
                       onChange={(e) => setTermsAccepted(e.target.checked)} />
                <span>
                  {t('acceptTerms')}{' '}
                  <a href={termsUrl} target="_blank" rel="noopener noreferrer">
                    {t('termsAndConditions')}
                  </a>
                </span>
              </label>
            </Card>
          )}

          <Button className="mx-3" disabled={submitting || !addressId || termsBlocked} onClick={placeOrder}>
            {submitting ? t('loading') : t('placeOrder')}
          </Button>
        </>
      )}

      <Toast showToast={showToast} setShowToast={setShowToast} message={t(error, { count: voucherLimit })} variant="error" />
    </Base>
  );
};

export default Checkout;
